import React, {memo, useCallback} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {MobileHeaderMenu} from 'components/MobileHeader/MobileHeaderMenu'
import {MyListHeaderEntry} from 'components/MyListHeaderEntry'
import {QuickFilters} from 'components/QuickFilters'
import {SearchBoxMobile} from 'components/SearchBox/Search'
import {
  SearchNewTopBar,
  SearchTopBarLogoWrapper
} from 'components/SearchNewTopBar'
import {SearchSummaryButtonWithStore} from 'components/SearchSummaryButtonWithStore'
import {useIsPageSearchBoxOpen} from 'hooks/searchBox'
import {useNewTopBar} from 'hooks/useNewTopBar'
import useShowOverlay from 'hooks/useShowOverlay/useShowOverlay'
import {getLanguageCode} from 'modules/meta/selectors'
import {OverlayType} from 'modules/overlay/types'
import {getAnchorCity, getSearchType} from 'modules/sapiSearch/selectors'
import {SearchType} from 'modules/sapiSearch/utils'
import {getDateValidationReason} from 'modules/search/selectors'
import {
  getIsSearchBoxOpen,
  getSearchBoxAncestor
} from 'modules/searchBox/selectors'
import {SearchBoxAncestor} from 'modules/searchBox/types'
import {getHomeUrlWithUTMParams} from 'utils/utmParams'

import {Box} from '@vio/atlas/helpers/Box'
import {useIsReferralProgramEnabled} from '@vio/atlas/hooks/useIsReferralProgramEnabled'
import {Icon} from '@vio/atlas/Icon'
import {NavBar} from '@vio/atlas/NavBar'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {useRouterProvider} from '@vio/core/src/_web/router/context/RouterContext'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Entity,
  Page,
  Team,
  TrackEventPayload
} from '@vio/core/src/analytics/types/Events'
import {getUser, selectAuthEnabled} from '@vio/core/src/auth/modules/selectors'
import {getLocalizedHelpUrl} from '@vio/core/src/brand/utils'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {useIsHydrated} from '@vio/core/src/utils/react/hooks/useIsHydrated'
import {NavBarBackButton, NavBarBrandLogo} from '@vio/shared/src/navBar'
import {MyListLinkComponent} from '@vio/shared/src/priceWatch/hooks/useTrackMyListLinkClick'
import {
  DateTooltipAnchor,
  SearchDateTooltipWrapper
} from '@vio/shared/src/search/SearchDateTooltipWrapper/SearchDateTooltipWrapper'
import {BrandLogo} from '@vio/shared/src/whiteLabel/BrandLogo/BrandLogo'

const BottomContentWrapper = styled.div`
  width: 100%;
`

const EndContentWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${cssTheme.layout.spacing.s200};
  color: ${cssTheme.colors.srp.topBar.content};
`

const SearchNewHeaderCenterContentWrapper = styled.div`
  width: 100%;

  ${DateTooltipAnchor} {
    left: 50px;
  }
`

const KiwiPlaceSearchTitle = styled.div`
  display: flex;
  position: relative;
  padding: ${cssTheme.layout.spacing.s400} ${cssTheme.layout.spacing.s500};
`

export interface MobileHeaderContentProps {
  className?: string
  hasDivider?: boolean
  hasHeaderSearchSummary?: boolean
  hasHeaderBottomContent?: boolean
  hasHeaderMenu?: boolean
  hasBackButton?: boolean
  hasSearchProgressBar?: boolean
  headerTitle?: React.ReactElement
  page?: Page
}

const MobileHeaderContentComponent = ({
  className,
  hasDivider = false,
  hasHeaderSearchSummary,
  hasHeaderBottomContent = false,
  hasHeaderMenu = true,
  hasBackButton = false,
  headerTitle,
  page
}: MobileHeaderContentProps) => {
  const dispatch = useDispatch()
  const {brand} = useBrand()
  const {search, shell, auth} = useBrandCapabilities()
  const user = useSelector(getUser)
  const showOverlay = useShowOverlay()
  const {push} = useRouterProvider()
  const authEnabled = useSelector(selectAuthEnabled)
  const isInviteFriendsTab = useIsReferralProgramEnabled(brand)
  const activeAncestor = useSelector(getSearchBoxAncestor)
  const searchBoxIsOpen =
    useSelector(getIsSearchBoxOpen) && SearchBoxAncestor.Page === activeAncestor

  const isPageSearchBoxOpen = useIsPageSearchBoxOpen()

  // SRP/mobile header = the same 2 brand flags as desktop (custom ⟹ collapsing):
  //   Vio default  !isCollapsing              static bar          [vio + most WLs]
  //   WL standard  isCollapsing && !isCustom  collapses on scroll [dealbase]
  //   WL custom    isCustom                   own <CustomHeader>  [kiwi, hp, finn]
  // (The page === Page.Search branch renders SearchNewTopBar instead of this bar.)
  const isCustom = shell.hasCustomHeader
  const isCollapsing = shell.hasCollapsingHeader
  // Our logo/back + menu row. Custom brands supply their own, so we drop it.
  const showChrome = !isCustom
  // dealbase's standard chrome bar that collapses on scroll.
  const hasCollapsingTopBar = isCollapsing && showChrome
  const hasWhiteText = !searchBoxIsOpen && shell.hasDarkMobileHeader

  const isHydrated = useIsHydrated()
  const homeURL = isHydrated ? getHomeUrlWithUTMParams() : '/'
  const languageCode = useSelector(getLanguageCode)
  const anchorCityName = useSelector(getAnchorCity)
  const searchType = useSelector(getSearchType)

  const {showDivider, showCompactHeader} = useNewTopBar()

  const onBackButtonClick = () => {
    push(homeURL)
  }

  const handleShowPreferences = () => {
    showOverlay({
      type: OverlayType.Preferences,
      entity: Entity.PreferencesOverlay,
      team: Team.Retention,
      payload: {
        isInviteFriendsTab
      }
    })
  }

  const shouldShowUserAvatar =
    auth.showMobileUserAvatar && authEnabled && !hasBackButton

  const onGetHelpClick = (e: React.MouseEvent) => {
    e.preventDefault()
    window.open(getLocalizedHelpUrl(brand, languageCode))
  }

  // Brands may pin the mobile logo to a fixed height (e.g. Kiwi's bulky logo);
  // otherwise fall back to the default responsive sizing.
  const logoHeight = shell.mobileLogoFixedHeight ?? (searchBoxIsOpen ? 30 : 20)

  const dateValidationReason = useSelector(getDateValidationReason)

  // Mobile only shows tooltips for validation reasons (not booking hints),
  // and validation tooltips always bypass the cookie check
  const showMobileDateTooltip = isHydrated && !!dateValidationReason

  const trackEventCallback = useCallback(
    (payload: TrackEventPayload) => dispatch(trackEvent(payload)),
    [dispatch]
  )

  const showsAnchorCityName =
    search.showsAnchorCityName &&
    (searchType === SearchType.Place || searchType === SearchType.IATA) &&
    Boolean(anchorCityName)

  if (page === Page.Search) {
    return (
      <SearchNewTopBar
        className={className}
        isSearchBoxOpen={searchBoxIsOpen}
        isCompact={showCompactHeader}
        hasDivider={showDivider}
        brandLogo={
          <SearchTopBarLogoWrapper brand={brand} logoUrl={homeURL}>
            <BrandLogo
              brand={brand}
              height={logoHeight}
              isInverse={hasWhiteText}
            />
          </SearchTopBarLogoWrapper>
        }
        centerContent={
          searchBoxIsOpen ? (
            <BottomContentWrapper data-id="SearchBoxMobile">
              <SearchBoxMobile
                openGuestPickerOnCheckOutChange={false}
                ancestor={SearchBoxAncestor.Page}
              />
            </BottomContentWrapper>
          ) : (
            <SearchNewHeaderCenterContentWrapper>
              {showsAnchorCityName && !showCompactHeader && (
                <KiwiPlaceSearchTitle>
                  <Text variant="titleXL" colorPath="neutral.c950">
                    <FormattedMessageWrapper
                      id="title.placePage"
                      defaultMessage="Hotels in {place}"
                      values={{
                        place: (
                          <Text
                            tag="span"
                            variant="titleXL"
                            colorPath="brand.c600"
                          >
                            {anchorCityName}
                          </Text>
                        )
                      }}
                    />
                  </Text>
                </KiwiPlaceSearchTitle>
              )}
              <SearchDateTooltipWrapper
                isDateConfirmationTooltipShown={showMobileDateTooltip}
                dateValidationReason={dateValidationReason}
                trackEvent={trackEventCallback}
              >
                <SearchSummaryButtonWithStore
                  ancestor={SearchBoxAncestor.Page}
                  isCompact={showCompactHeader}
                  withSidePadding={false}
                />
              </SearchDateTooltipWrapper>
            </SearchNewHeaderCenterContentWrapper>
          )
        }
        endContent={
          <EndContentWrapper>
            {!searchBoxIsOpen && (
              <MyListHeaderEntry
                component={MyListLinkComponent.MobileHeader}
                iconOnly
                variant={hasWhiteText ? 'ghostInverse' : 'ghost'}
              />
            )}
            {!searchBoxIsOpen && (
              <Box padding="s300" onClick={onGetHelpClick}>
                <Icon
                  name="HelpCircle"
                  size="lg"
                  colorPath={
                    shell.hasNeutralMobileHeaderIcon
                      ? 'content.neutral.primary'
                      : undefined
                  }
                />
              </Box>
            )}
            <MobileHeaderMenu
              hasInverseHeader={hasWhiteText}
              hasNeutralMobileMenuText={shell.hasNeutralMobileMenuText}
              shouldShowUserAvatar={shouldShowUserAvatar}
              user={user}
              onShowPreferences={handleShowPreferences}
            />
          </EndContentWrapper>
        }
        bottomContent={
          !searchBoxIsOpen && search.hasFilters ? <QuickFilters /> : undefined
        }
      />
    )
  }

  // Custom-header brands render their branding from the page layout, so this
  // NavBar only renders once there's a search row to host; everyone else always
  // renders the logo/menu chrome.
  if (!showChrome && !hasHeaderBottomContent) {
    return null
  }

  // The logo + menu row only exists when we own the chrome.
  const showMenu = showChrome && hasHeaderMenu
  const showTitle = hasBackButton && Boolean(headerTitle)
  const showSearchBox = hasHeaderBottomContent && searchBoxIsOpen

  // The search box always lives in the centerContent: the compact summary when
  // collapsed, the full search box (dropped to its own row via wrapCenterContent)
  // when expanded. A back-button page title takes precedence over both.
  const centerContent = showTitle ? (
    headerTitle
  ) : showSearchBox ? (
    <BottomContentWrapper data-id="SearchBoxMobile">
      <SearchBoxMobile
        openGuestPickerOnCheckOutChange={false}
        ancestor={SearchBoxAncestor.Page}
      />
    </BottomContentWrapper>
  ) : hasHeaderSearchSummary && !isPageSearchBoxOpen ? (
    <SearchSummaryButtonWithStore
      ancestor={SearchBoxAncestor.Page}
      withSidePadding={!isCollapsing}
    />
  ) : undefined

  const startContent = showChrome ? (
    hasBackButton ? (
      <NavBarBackButton onClick={onBackButtonClick} />
    ) : (
      <NavBarBrandLogo
        brand={brand}
        logoUrl={homeURL}
        BrandLogoComponent={BrandLogo}
      />
    )
  ) : undefined

  return (
    <NavBar
      className={className}
      withDivider={hasCollapsingTopBar || hasDivider}
      wrapCenterContent={showSearchBox}
      centerAligned={showTitle}
      startContent={startContent}
      centerContent={centerContent}
      endContent={
        showMenu ? (
          <MobileHeaderMenu
            shouldShowUserAvatar={shouldShowUserAvatar}
            user={user}
            onShowPreferences={handleShowPreferences}
          />
        ) : undefined
      }
      bottomContent={
        hasHeaderBottomContent && !searchBoxIsOpen && search.hasFilters ? (
          <QuickFilters />
        ) : undefined
      }
    />
  )
}

export const MobileHeaderContent = memo(MobileHeaderContentComponent)
